import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, TrendingUp } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { getRisingForums } from '../utils/forumGrowth';
import { usePageSeo } from '../hooks/usePageSeo';
import { absoluteUrl } from '../utils/seo';
import type { ForumBase, GrowthMetric } from '../types/forum';
interface Props {
  darkMode: boolean;
}

const RisingForumsPage: React.FC<Props> = ({ darkMode }) => {
  const [forums, setForums] = useState<ForumBase[]>([]);
  const [metric, setMetric] = useState<GrowthMetric>('members');
  const [loading, setLoading] = useState(true);

  usePageSeo({
    title: 'Yükselen forumlar',
    description: 'Üye, konu ve ileti artışına göre en hızlı büyüyen Türk forumları.',
    keywords: 'yükselen forumlar, büyüyen forumlar, forum büyüme istatistikleri',
    canonical: absoluteUrl('/rising'),
  });

  useEffect(() => {
    supabase
      .from('forums')
      .select('*')
      .eq('is_active', true)
      .then(({ data }) => {
        setForums(data || []);
        setLoading(false);
      });
  }, []);

  const rising = getRisingForums(forums, metric, 50);

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-50'}`}>
      <div className="container mx-auto px-4 py-8">
        <Link to="/" className={`inline-flex items-center gap-2 mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          <ArrowLeft size={20} /> Geri
        </Link>
        <h1 className="text-2xl font-bold mb-4 flex items-center gap-2">
          <TrendingUp className="text-green-500" /> Yükselen forumlar
        </h1>
        <div className="flex flex-wrap gap-2 mb-6">
          {(['members', 'topics', 'posts', 'score'] as GrowthMetric[]).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMetric(m)}
              className={`px-3 py-1.5 rounded-lg text-sm ${
                metric === m ? 'bg-indigo-600 text-white' : darkMode ? 'bg-gray-800' : 'bg-white border'
              }`}
            >
              {m === 'members' ? 'Üye' : m === 'topics' ? 'Konu' : m === 'posts' ? 'İleti' : 'Skor'}
            </button>
          ))}
        </div>
        {loading ? (
          <div className="animate-spin h-10 w-10 border-b-2 border-indigo-500 rounded-full" />
        ) : (
          <ul className="space-y-2">
            {rising.map((f, i) => (
              <li key={f.id}>
                <Link
                  to={`/forum/${f.forum_id}`}
                  className={`flex justify-between p-4 rounded-lg ${darkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-white shadow-sm'}`}
                >
                  <span>
                    #{i + 1} {f.name}
                  </span>
                  <span className="text-green-500 font-medium">+{f.growthPercent.toFixed(1)}%</span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default RisingForumsPage;
